import React from 'react';

import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { useField, useFormState } from 'react-final-form';

import { Tooltip } from 'src/components/common';

import { BULK_LOCKED_TOOLTIP, PeoLiveStatusFormField } from '../constants';
import { PeoLiveStatusValues } from '../types';

type PeoEmployersLiveStatusBulkButtonProps = {
  employersCount: number;
  liveEmployersCount: number;
};

/**
 * Sets the pending bulk change for every Employer under the PEO. Locked while there are
 * individual Employer changes, and locks the individual switches while a bulk change is pending.
 */
export const PeoEmployersLiveStatusBulkButton: React.FC<PeoEmployersLiveStatusBulkButtonProps> = ({
  employersCount,
  liveEmployersCount,
}) => {
  const { input } = useField<PeoLiveStatusValues['setAllLive']>(PeoLiveStatusFormField.setAllLive, {
    allowNull: true,
    subscription: { value: true },
  });
  const { values, submitting } = useFormState<PeoLiveStatusValues>({
    subscription: { values: true, submitting: true },
  });
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const hasEmployerChanges = !isEmpty(values[PeoLiveStatusFormField.liveStatusChanges]);

  // `null` when some Employers are Live and some are not
  const savedAllLive: boolean | null = React.useMemo(() => {
    if (liveEmployersCount === 0) return false;
    if (liveEmployersCount === employersCount) return true;
    return null;
  }, [employersCount, liveEmployersCount]);

  const pendingAllLive = isNotNil(input.value) ? input.value : savedAllLive;

  const handleSetAllLive = React.useCallback(
    (isLive: boolean) => {
      setAnchorEl(null);
      // No bulk update is needed when the target matches what is already saved
      input.onChange(isLive === savedAllLive ? null : isLive);
    },
    [input, savedAllLive],
  );

  const handleOpenMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);
  const handleCloseMenu = React.useCallback(() => setAnchorEl(null), []);
  const handleMakeAllLive = React.useCallback(() => handleSetAllLive(true), [handleSetAllLive]);
  const handleMakeAllNotLive = React.useCallback(() => handleSetAllLive(false), [handleSetAllLive]);
  const handleToggleAll = React.useCallback(
    () => handleSetAllLive(!pendingAllLive),
    [handleSetAllLive, pendingAllLive],
  );

  const disabled = submitting || employersCount === 0 || hasEmployerChanges;

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
      <Tooltip title={hasEmployerChanges ? BULK_LOCKED_TOOLTIP : ''} placement="top" arrow>
        {/* A disabled button fires no events, so the wrapper is the tooltip anchor */}
        <Box component="span" display="inline-block">
          {pendingAllLive === null ? (
            <Button
              variant="outlined"
              endIcon={<ArrowDropDownIcon />}
              disabled={disabled}
              onClick={handleOpenMenu}
            >
              Make All
            </Button>
          ) : (
            <Button variant="outlined" disabled={disabled} onClick={handleToggleAll}>
              {pendingAllLive ? 'Make All Not Live' : 'Make All Live'}
            </Button>
          )}
        </Box>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleMakeAllLive}>Live</MenuItem>
        <MenuItem onClick={handleMakeAllNotLive}>Not Live</MenuItem>
      </Menu>

      {isNotNil(input.value) && (
        <Typography variant="caption" color="text.secondary">
          All Employers will be set {input.value ? 'Live' : 'Not Live'} on save
        </Typography>
      )}
    </Box>
  );
};
